import { z } from "zod";
import {
  HOUR_MS,
  dayLabel,
  idSchema,
  instantSchema,
  sourceDateSchema,
  type DateBasis,
  type SourceDate,
} from "@amber/contracts";

export const SOURCE_DATE_BASIS_LABELS: Record<DateBasis, string> = {
  publisher_metadata: "publisher metadata",
  official_record: "official record",
  page_byline: "page byline",
  feed: "feed",
  publication_proxy: "publication date, used as date",
  editor: "given by an Editor",
  reference_data: "reference data",
  news_supplier: "news supplier",
  secondary_report: "stated in a news report",
};

export const dateClaimSchema = z.strictObject({
  date: sourceDateSchema,
  label: z.string(),
  item_id: idSchema("itm").nullable(),
});
export type DateClaim = z.infer<typeof dateClaimSchema>;

export const dateConflictSchema = z.strictObject({
  key: z.string().min(1),
  claims: z.tuple([dateClaimSchema, dateClaimSchema]),
  detected_at: instantSchema,
  task_id: idSchema("tsk").nullable(),
  resolved_at: instantSchema.nullable(),
});
export type DateConflict = z.infer<typeof dateConflictSchema>;

export const dateConflictViewSchema = z.strictObject({
  text: z.string(),
  claims: z.array(dateClaimSchema),
  task_id: idSchema("tsk").nullable(),
});
export type DateConflictView = z.infer<typeof dateConflictViewSchema>;

/** DI-65: an unknown event date takes the publication date, labelled as a proxy. */
export function publicationProxy(published: SourceDate): SourceDate {
  return { ...published, basis: "publication_proxy" };
}

export function isPublicationProxy(date: SourceDate | null): boolean {
  return date?.basis === "publication_proxy";
}

const LEVELS = ["year", "month", "day"] as const;
type Level = (typeof LEVELS)[number];
const PREFIX: Record<Level, number> = { year: 4, month: 7, day: 10 };

const RANK: Record<SourceDate["precision"], number> = { year: 0, month: 1, day: 2, minute: 2, second: 2 };

function levelOf(a: SourceDate, b: SourceDate): Level {
  return LEVELS[Math.min(RANK[a.precision], RANK[b.precision])] ?? "day";
}

const isInstant = (d: SourceDate) => d.value.length > 10 && d.value.includes("T");

function dayIn(ms: number, timezone: string): string | null {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: timezone, dateStyle: "short" }).format(ms);
  } catch {
    return null;
  }
}

// Civil time zones run from UTC−12 to UTC+14, so an instant without a known local zone could fall on any of these days.
const OFFSETS_MS = [-12 * HOUR_MS, 0, 14 * HOUR_MS];

function days(date: SourceDate, other: SourceDate): string[] {
  const ms = Date.parse(date.value);
  if (!isInstant(date) || Number.isNaN(ms)) return [date.value];
  const zone = !isInstant(other) ? other.timezone : null;
  if (zone) {
    const known = [dayIn(ms, zone), date.timezone ? dayIn(ms, date.timezone) : null];
    const found = known.filter((d): d is string => d !== null);
    if (found.length > 0) return found;
  }
  return OFFSETS_MS.map((offset) => new Date(ms + offset).toISOString().slice(0, 10));
}

function candidates(date: SourceDate, other: SourceDate, level: Level): string[] {
  return [...new Set(days(date, other).map((d) => d.slice(0, PREFIX[level])))].sort();
}

/** DI-65: two event dates disagree when no reading of either, at the coarser precision (at most a day), matches. */
export function datesConflict(a: SourceDate, b: SourceDate): boolean {
  const level = levelOf(a, b);
  const there = new Set(candidates(b, a, level));
  return !candidates(a, b, level).some((d) => there.has(d));
}

/** DI-65: a communication can't take place after it was published. */
export function occursAfterPublication(event: SourceDate, published: SourceDate): boolean {
  const level = levelOf(event, published);
  const earliest = candidates(event, published, level)[0];
  const latest = candidates(published, event, level).at(-1);
  return earliest !== undefined && latest !== undefined && earliest > latest;
}

export function describeSourceDate(date: SourceDate): string {
  if (date.precision === "year") return date.value.slice(0, 4);
  const at = new Date(`${date.value.slice(0, 10)}T12:00:00.000Z`);
  if (date.precision === "month") return at.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  return dayLabel(at, { year: true });
}

export function dateConflictText(claims: readonly Pick<DateClaim, "date" | "label">[]): string {
  const sameBasis = new Set(claims.map((c) => c.date.basis)).size < claims.length;
  const part = (c: Pick<DateClaim, "date" | "label">) =>
    `${describeSourceDate(c.date)} (${SOURCE_DATE_BASIS_LABELS[c.date.basis]}${sameBasis ? `, ${c.label}` : ""})`;
  return `Dates disagree: ${claims.map(part).join(" vs ")}`;
}
